import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const PLATFORMS = [
  { id: "emergent", title: "Emergent site", hint: "Spanish at clientsite.com/es/…" },
  { id: "ghl", title: "GoHighLevel site or funnel", hint: "Spanish at es.clientsite.com" },
];

export const AddWebsiteDialog = ({ atLimit, max, onCreated }) => {
  const { session, config } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "", platform: "emergent" });
  const [busy, setBusy] = useState(false);

  if (atLimit) {
    return (
      <div className="flex flex-col items-start lg:items-end gap-1.5" data-testid="website-limit-reached">
        <Button disabled data-testid="add-website-button"><Plus className="h-4 w-4 mr-1.5" /> Add website</Button>
        <span className="text-xs text-slate-600">Your plan covers {max} website{Number(max) === 1 ? "" : "s"}.{" "}
          <a href={config?.plans_url} target="_blank" rel="noreferrer" className="text-blue-600 underline inline-flex items-center gap-0.5" data-testid="plans-link">Add one in Plans <ExternalLink className="h-3 w-3" /></a>
        </span>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/sites", form);
      toast.success("Website added");
      setOpen(false);
      onCreated?.();
      navigate(`/sites/${r.data.id}?tab=setup`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={!session.can_edit} data-testid="add-website-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> Add website</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="add-website-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add a website</DialogTitle>
          <DialogDescription>$12 per website per month, with 50,000 words included.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PLATFORMS.map((p) => (
              <button type="button" key={p.id} data-testid={`platform-option-${p.id}`} onClick={() => setForm({ ...form, platform: p.id })}
                className={`text-left p-3 rounded-md border transition-colors duration-150 ${form.platform === p.id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}>
                <div className="text-sm font-medium">{p.title}</div>
                <div className="text-xs text-slate-500 font-mono mt-1">{p.hint}</div>
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="site-name">Website name</Label>
            <Input id="site-name" data-testid="site-name-input" placeholder="Casa Verde Landscaping" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="site-domain">Domain</Label>
            <Input id="site-domain" data-testid="site-domain-input" placeholder="clientsite.com" className="font-mono" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} required />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="add-website-submit">{busy ? "Adding…" : "Add website"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
